import { useEffect, useCallback, useRef } from "react";

const STORAGE_KEY = "tracking_draft_entries";
const DEBOUNCE_DELAY = 500;
const EXPIRATION_MS = 24 * 60 * 60 * 1000;

const useDraftEntries = (entries, setEntries, isDataLoaded) => {
    const debounceRef = useRef(null);
    const hasRestoredRef = useRef(false);
    const isRestoringRef = useRef(false);

    useEffect(() => {
        if (!isDataLoaded || hasRestoredRef.current) return;

        hasRestoredRef.current = true;
        isRestoringRef.current = true;

        try {
            const stored = localStorage.getItem(STORAGE_KEY);
            if (stored) {
                const { drafts, savedAt } = JSON.parse(stored);
                const isExpired = !savedAt || Date.now() - savedAt > EXPIRATION_MS;

                if (isExpired) {
                    localStorage.removeItem(STORAGE_KEY);
                } else if (Array.isArray(drafts) && drafts.length > 0) {
                    setEntries((prev) => {
                        const nonTempEntries = prev.filter((e) => !e.tempId);
                        const restoredDrafts = drafts.map((draft, index) => ({
                            ...draft,
                            tempId: Date.now() + index,
                            isEditing: true,
                        }));
                        return [...nonTempEntries, ...restoredDrafts];
                    });
                }
            }
        } catch (e) {
            localStorage.removeItem(STORAGE_KEY);
            console.error("Error restoring draft entries", e);
        }

        setTimeout(() => {
            isRestoringRef.current = false;
        }, 100);
    }, [isDataLoaded, setEntries]);

    const saveDrafts = useCallback((currentEntries) => {
        const tempEntries = currentEntries.filter((e) => e.tempId);

        if (tempEntries.length === 0) {
            localStorage.removeItem(STORAGE_KEY);
            return;
        }

        const draftsToStore = tempEntries.map((e) => ({
            date: e.date,
            projectId: e.projectId,
            taskId: e.taskId,
            comments: e.comments,
            duration: e.duration,
            customOrder: e.customOrder,
        }));

        localStorage.setItem(STORAGE_KEY, JSON.stringify({ drafts: draftsToStore, savedAt: Date.now() }));
    }, []);

    useEffect(() => {
        if (!hasRestoredRef.current || isRestoringRef.current) return;

        if (debounceRef.current) {
            clearTimeout(debounceRef.current);
        }

        debounceRef.current = setTimeout(() => {
            saveDrafts(entries);
        }, DEBOUNCE_DELAY);

        return () => {
            if (debounceRef.current) {
                clearTimeout(debounceRef.current);
            }
        };
    }, [entries, saveDrafts]);

    const clearAllDrafts = useCallback(() => {
        localStorage.removeItem(STORAGE_KEY);
    }, []);

    return { clearAllDrafts };
};

export default useDraftEntries;
